<template lang="pug">
  #outer-card
    .companion-action-menu
      CompanionActionsMenu
    .green-card
      .music-player
        .cover-art
          img(src="https://stage-bewell.sitesstage.com/media/wyabgqbk/guide-player_breathe-relax-morning-breathing-3x.png", alt="Album cover")
        .track-info
          p {{ audioGuidePartNo }} - {{ audioGuideTitle }} 
          h3
            .chapters-info-title
              WtSvgIcon( icon="companion/time" width="18px", height="18px")
              span {{ audioGuideDuriation }} | {{ numberOfChapters }} chapters
          p.mt-5 
            span {{ currentChapterTitle }}
        .progress-container
          input.custom-slider(
            class="slider"
            type="range"
            v-model="progress"
            @mouseup="updateProgressAndSeek"
            :min="0"
            :max="duration"
            :style="{ '--progress': (progress / duration) * 100 + '%' }"
          )
          .time-info.pt-2
            span {{ formatTime(progress) }}
            span {{ formatTime(duration) }}
        .controls
          WtSvgIcon(@click.native="goToPreviousChapter", icon="companion/skip_backward", width="40px", height="40px")
          WtSvgIcon(@click.native="skipBackward", icon="companion/skip_backward_15sec", width="40px", height="40px")
          img(v-if="isPlaying" @click="togglePlay", src="@@@/companion/pause.svg", width="56", height="56")
          img(v-else @click="togglePlay", src="@@@/companion/play.svg", width="56", height="56")
          WtSvgIcon(@click.native="skipForward", icon="companion/skip_forward_15sec", width="40px", height="40px")
          WtSvgIcon(@click.native="goToNextChapter", icon="companion/skip_forward", width="40px", height="40px")
</template>

<script>
import WtButton from "@@/WtButton/WtButton.vue";
import WtTile from "@@/WtTile/WtTile";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";
import CompanionActionsMenu from "@@/Companion/CompanionActionsMenu";

import { Howl } from "howler";

export default {
  name: "CompanionAudioGuide",

  components: {
    CompanionActionsMenu,
    WtButton,
    WtTile,
    WtSvgIcon
  },

  props: {},

  data() {
    return {
      sound: null,
      isPlaying: false,
      progress: 0,
      duration: 0,
      isSeeking: false, // Flag to indicate if user is seeking with the slider
      audioGuidePartNo: "Part 1",
      audioGuideTitle: "Morning breathing",
      audioGuideDuriation: "7 mins",
      numberOfChapters: 5,
      chapterTimeStamps: [0, 100, 200, 300, 400],
      chapterTitles: ["Round 1", "Round 2", "Round 3", "Round 4", "Round 5"],
      audioFileUrl:
        "https://actions.google.com/sounds/v1/ambiences/carnival_atmosphere.ogg"
    };
  },

  computed: {
    currentChapterTitle() {
      const currentPosition = this.sound.seek() || 0;

      for (let i = 0; i < this.chapterTimeStamps.length; i++) {
        const currentTimestamp = this.chapterTimeStamps[i];
        const nextTimestamp = this.chapterTimeStamps[i + 1];

        if (
          currentPosition >= currentTimestamp &&
          (nextTimestamp === undefined || currentPosition < nextTimestamp)
        ) {
          return this.chapterTitles[i];
        }
      }
      return "";
    }
  },

  watch: {
    "$route.params.chapterNumber"(newChapterNumber) {
      // Fetch the chapter number from params
      const chapterNumber = parseInt(newChapterNumber);

      // Check if the chapter number is valid
      if (
        !isNaN(chapterNumber) &&
        chapterNumber >= 1 &&
        chapterNumber <= this.numberOfChapters
      ) {
        // Get the timestamp of the chapter
        const chapterTimestamp = this.chapterTimeStamps[chapterNumber - 1];

        // Seek to the timestamp of the chapter
        this.seekTo(chapterTimestamp);

        this.sound.play();
        this.isPlaying = true;
      }
    }
  },

  onmount() {
    if (this.sound && !this.isPlaying) {
      this.sound.play();
      this.isPlaying = true;
    }
  },

  created() {
    // Initialize the audio player
    this.sound = new Howl({
      src: [this.audioFileUrl],
      onload: () => {
        // Fetch the chapter number from params (assuming you have access to params)
        const chapterNumber = parseInt(this.$route.params.chapterNumber);

        // Check if the chapter number is valid
        if (
          !isNaN(chapterNumber) &&
          chapterNumber >= 1 &&
          chapterNumber <= this.numberOfChapters
        ) {
          // Get the timestamp of the chapter
          const chapterTimestamp = this.chapterTimeStamps[chapterNumber - 1];

          // Seek to the timestamp of the chapter
          this.seekTo(chapterTimestamp);
        }
        // Set the duration and start updating progress
        this.duration = this.sound.duration();
        this.updateProgress();
      },
      onloaderror: error => {
        console.error("Audio loading error:", error);
      }
    });
  },

  beforeDestroy() {
    if (this.sound) {
      this.sound.unload();
    }
  },

  methods: {
    formatTime(seconds) {
      const pad = (num, size) => ("000" + num).slice(size * -1);
      const time = parseFloat(seconds).toFixed(3);
      const hours = Math.floor(time / 60 / 60);
      const minutes = Math.floor(time / 60) % 60;
      const secondsLeft = Math.floor(time - minutes * 60);

      if (hours) {
        return (
          pad(hours, 2) + ":" + pad(minutes, 2) + ":" + pad(secondsLeft, 2)
        );
      } else {
        return pad(minutes, 2) + ":" + pad(secondsLeft, 2);
      }
    },

    togglePlay() {
      if (this.sound && this.sound.playing()) {
        this.sound.pause();
      } else {
        this.sound.play();
      }
      this.isPlaying = !this.isPlaying; // Toggle the isPlaying state
    },

    seekTo(value) {
      this.isSeeking = true;
      const isPlaying = this.sound.playing();
      if (isPlaying) {
        this.sound.pause();
      }
      this.sound.seek(value);
      this.progress = value;
      if (isPlaying) {
        this.sound.play();
      }
      this.isSeeking = false;
    },

    skipBackward() {
      const currentPosition = this.sound.seek() || 0;
      const newPosition = Math.max(currentPosition - 15, 0); // Ensure newPosition >= 0
      this.seekTo(newPosition);
    },

    skipForward() {
      const currentPosition = this.sound.seek() || 0;
      const newPosition = Math.min(currentPosition + 15, this.duration); // Ensure newPosition <= duration
      this.seekTo(newPosition);
    },

    goToPreviousChapter() {
      const currentPosition = this.sound.seek() || 0;
      const currentChapterIndex = this.chapterTimeStamps.findIndex(
        (timestamp, index, array) => {
          const nextTimestamp = array[index + 1];
          return (
            currentPosition >= timestamp &&
            (nextTimestamp === undefined || currentPosition < nextTimestamp)
          );
        }
      );

      if (currentChapterIndex >= 0) {
        const previousChapterTimestamp = this.chapterTimeStamps[
          currentChapterIndex - 1
        ];
        this.seekTo(previousChapterTimestamp);
      } else {
        // If already at the beginning, go to the start of the current chapter
        this.seekTo(0);
      }
    },

    goToNextChapter() {
      const currentPosition = this.sound.seek() || 0;
      const currentChapterIndex = this.chapterTimeStamps.findIndex(
        (timestamp, index, array) => {
          return (
            currentPosition >= timestamp &&
            (array[index + 1] === undefined ||
              currentPosition < array[index + 1])
          );
        }
      );

      if (currentChapterIndex >= 0) {
        const nextChapterTimestamp = this.chapterTimeStamps[
          currentChapterIndex + 1
        ];
        if (nextChapterTimestamp !== undefined) {
          this.seekTo(nextChapterTimestamp);
        } else {
          // If already at the end, stay at the end of the current chapter
          this.seekTo(this.duration);
        }
      } else {
        // If not within any chapter, seek to the beginning of the first chapter
        this.seekTo(this.chapterTimeStamps[0]);
      }
    },

    onSliderChange(value) {
      this.progress = value;
    },

    updateProgressAndSeek() {
      const isPlaying = this.sound.playing();
      if (isPlaying) {
        this.sound.pause();
      }
      this.sound.seek(this.progress);
      if (isPlaying) {
        this.sound.play();
      }
    },

    updateProgress() {
      if (this.sound && this.sound.playing() && !this.isSeeking) {
        this.progress = this.sound.seek();
      }
      setTimeout(this.updateProgress, 1000);
    }
  }
};
</script>

<style scoped>
#outer-card {
  background-color: white;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 70%;
}

.music-player {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.green-card {
  background-color: #a4d8cb;
  border-radius: 16px;
}

.green-card {
  background-color: #a4d8cb;
}

.player-header,
.track-info,
.controls,
.progress-bar,
.chapter-controls {
  margin-bottom: 5px;
  color: white;
}

* {
  font-weight: bold;
}

.cover-art img {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover;
}

.controls,
.chapter-controls {
  display: flex;
  justify-content: center;
  gap: 20px;
  align-items: center;
}

.track-info h3,
.track-info p {
  text-align: center;
  color: white;
}

.track-info div {
  display: flex;
  align-items: center;
  justify-content: center;
}

.track-info div WtTile {
  margin-right: 10px;
}

.track-info div WtSvgIcon {
  vertical-align: middle;
}

.custom-slider {
  -webkit-appearance: none;
  width: 100%;
  height: 3px;
  border-radius: 5px;
  background: linear-gradient(
    to right,
    white 0%,
    white var(--progress),
    rgba(255, 255, 255, 0.4) var(--progress),
    rgba(255, 255, 255, 0.4) 100%
  );
  outline: none;
  -webkit-transition: 0.2s;
  transition: opacity 1s;
}

.custom-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  cursor: pointer;
  opacity: 1;
}

.chapters-info-title {
  display: flex;
  justify-content: center;
  gap: 2px;
  align-items: center;
}

.progress-container {
  width: calc(50%);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 10px;
}

.time-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  color: white;
}
</style>
